var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { DeleteModelMessage, InteractionFacet, SendToServerMessage, LocalTransformFacet, UpdateFacetOnModelMessage, convertToNumber3, convertToNumber4, RoomChangedMessage, UpdateGizmoTransformMessage, UpdateGizmoModeMessage, isFacetType, EnableModelTransformHandlesAction } from "flavr-common";
import { Euler, Matrix4, Quaternion, Vector3 } from "flavr-math";
import { ModelClickedMessage, NoModelClickedMessage } from "@messages/interaction/InteractionMessages";
import { busService } from "@modules/bus";
import { modelPool, computeLocalMatrixFromWorldMatrix } from "@modules/model";
const connection = busService.connection("InteractionModelsStore");
export function decomposeMatrix4(matrix) {
    const pos = new Vector3();
    const scale = new Vector3();
    const rot = new Quaternion();
    matrix.decompose(pos, rot, scale);
    return { position: pos, rotation: new Euler().setFromQuaternion(rot), scale, quaternion: rot };
}
export function composeMatrix4(coordinates) {
    const m4 = new Matrix4();
    m4.compose(coordinates.position, new Quaternion().setFromEuler(coordinates.rotation), coordinates.scale);
    return m4;
}
export const useInteractionModelsStore = create((set) => ({
    models: [],
    setModels: (models) => {
        set({ models });
    },
    updateModel: (model, sendUpdates = true) => {
        set((state) => ({ models: state.models.map((m) => (model.helper !== undefined && m.helper.getUID() === model.helper.getUID() ? model : m)) }));
        if (sendUpdates) {
            sendModelUpdatedMessages(model);
        }
    },
    currentModelUID: undefined,
    setCurrentModelUID: (currentModelUID) => {
        set({ currentModelUID });
    }
}));
function updateModels() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const currentRoom = modelPool.getCurrentRoom();
        if (currentRoom === undefined) {
            return;
        }
        const models = (_a = modelPool
            .getCurrentRoom()) === null || _a === void 0 ? void 0 : _a.getChildrenInRoom().filter(model => modelIsTransformer(model)).map(model => {
            return {
                helper: model,
                matrix: model.getWorldTransform()
            };
        });
        if (models !== undefined)
            useInteractionModelsStore.getState().setModels(models);
    });
}
function sendModelUpdatedMessages(interactionModel) {
    var _a;
    const m4 = (_a = interactionModel.matrix) !== null && _a !== void 0 ? _a : new Matrix4();
    const model = interactionModel.helper;
    const localMatrix = computeLocalMatrixFromWorldMatrix(model, m4);
    model.setTransform(localMatrix, { peer: true });
    modelPool.flushMessages();
    const transform = decomposeMatrix4(m4);
    connection.send(new UpdateGizmoTransformMessage(model.getUID(), convertToNumber3(transform.position), convertToNumber4(transform.quaternion), convertToNumber3(transform.scale)));
    connection.send(new SendToServerMessage([
        new UpdateFacetOnModelMessage(model.getUID(), new LocalTransformFacet(convertToNumber3(transform.position), convertToNumber4(transform.quaternion), convertToNumber3(transform.scale)))
    ]));
}
export function sendCoordinateTypeMessage(modelUid, isLocal) {
    connection.send(new UpdateGizmoModeMessage(modelUid, -1, isLocal));
}
let facetHandler;
let unsubscribeFromModelTransformEvents;
let roomChangedHandler;
let modelClickedHandler;
let noModelClickedHandler;
let deleteModelHandler;
export function startInteractionModelsStoreListeners() {
    return __awaiter(this, void 0, void 0, function* () {
        yield updateModels();
        if (facetHandler === undefined) {
            facetHandler = connection.subscribeByFacetType(InteractionFacet, (_uid, _facet) => __awaiter(this, void 0, void 0, function* () {
                yield updateModels();
            }));
        }
        else {
            console.warn("[ui] InteractionModelsStore: facetHandler already defined");
        }
        if (roomChangedHandler === undefined) {
            roomChangedHandler = connection.subscribeByType(RoomChangedMessage, () => __awaiter(this, void 0, void 0, function* () {
                yield updateModels();
            }));
        }
        if (unsubscribeFromModelTransformEvents === undefined) {
            unsubscribeFromModelTransformEvents = modelPool.setOnModelTransformChangedHandler((model, _transform) => {
                var _a;
                const changedModel = useInteractionModelsStore
                    .getState()
                    .models
                    .find((interactionModel) => { var _a; return ((_a = interactionModel.helper) === null || _a === void 0 ? void 0 : _a.getUID()) === model.getUID(); });
                if (changedModel !== undefined) {
                    const m4 = model.getWorldTransform();
                    if (((_a = changedModel.matrix) === null || _a === void 0 ? void 0 : _a.equals(m4)) === false) {
                        const newModel = Object.assign({}, changedModel);
                        newModel.matrix = m4;
                        useInteractionModelsStore.getState().updateModel(newModel, false);
                    }
                }
            });
        }
        else {
            console.warn("[ui] InteractionModelsStore: transformHandler already defined");
        }
        if (modelClickedHandler === undefined) {
            modelClickedHandler = connection.subscribeByType(ModelClickedMessage, (msg) => {
                const clickedModel = useInteractionModelsStore.getState().models.find((model) => model.helper !== undefined && model.helper.getUID() === msg.uid);
                if (clickedModel !== undefined) {
                    useInteractionModelsStore.getState().setCurrentModelUID(msg.uid);
                }
            });
        }
        else {
            console.warn("[ui] InteractionModelsStore: modelClickedHandler already defined");
        }
        if (noModelClickedHandler === undefined) {
            noModelClickedHandler = connection.subscribeByType(NoModelClickedMessage, () => {
                useInteractionModelsStore.getState().setCurrentModelUID(undefined);
            });
        }
        else {
            console.warn("[ui] InteractionModelsStore: noModelClickedHandler already defined");
        }
        if (deleteModelHandler === undefined) {
            connection.subscribeByType(DeleteModelMessage, (msg) => {
                const newModels = useInteractionModelsStore.getState().models;
                const index = newModels.findIndex((model) => model.helper !== undefined && model.helper.getUID() === msg.uid);
                if (index >= 0) {
                    newModels.splice(index, 1);
                    useInteractionModelsStore.getState().setModels(newModels);
                    if (useInteractionModelsStore.getState().currentModelUID === msg.uid) {
                        useInteractionModelsStore.getState().setCurrentModelUID(undefined);
                    }
                }
            });
        }
        else {
            console.warn("[ui] InteractionModelsStore: deleteModelHandler already defined");
        }
    });
}
export function stopInteractionModelsStoreListeners() {
    if (facetHandler !== undefined) {
        connection.unsubscribe(facetHandler);
        facetHandler = undefined;
    }
    if (unsubscribeFromModelTransformEvents !== undefined) {
        unsubscribeFromModelTransformEvents();
        unsubscribeFromModelTransformEvents = undefined;
    }
    if (roomChangedHandler !== undefined) {
        connection.unsubscribe(roomChangedHandler);
        roomChangedHandler = undefined;
    }
    if (modelClickedHandler !== undefined) {
        connection.unsubscribe(modelClickedHandler);
        modelClickedHandler = undefined;
    }
    if (noModelClickedHandler !== undefined) {
        connection.unsubscribe(noModelClickedHandler);
        noModelClickedHandler = undefined;
    }
    if (deleteModelHandler !== undefined) {
        connection.unsubscribe(deleteModelHandler);
        deleteModelHandler = undefined;
    }
}
const modelIsTransformer = (model) => {
    const facet = model.getFacet(InteractionFacet);
    return facet !== undefined &&
        isFacetType(InteractionFacet, facet) &&
        facet.onClick.find((actionParameter) => EnableModelTransformHandlesAction.is(actionParameter)) !== undefined;
};
